import React, { useState } from 'react';
import { get, isEmpty } from 'lodash';
import { AlertModal, Table } from 'src/componentsTailwind/index';
import { TYPES } from 'src/componentsTailwind/Table/Table';
import { useAccount } from 'src/queries/account';
import { QUERY_KEYS as FEEDBACK_QUERY_KEYS } from 'src/queries/feedback';
import CircleV2 from 'src/components/Circle/CircleV2';
import { appUtils, Base } from 'src/components/index';
import UpcomingFeedbacksFilters from 'src/containers/UserProfile/Activity/components/UpcomingFeedbacksFilters';
import FeedbackFlyout from 'src/containers/UserProfile/FeedbackDashboard/components/FeedbackFlyout';
import { useUserFeedback } from 'src/containers/UserProfile/FeedbackDashboard/queries';
import COMMON_CONSTANTS from 'common/commonConstants';
import commonDateUtils from 'common/commonDateUtils';
import commonUtils from 'common/commonUtils';
import STYLE from 'src/constants/style';

const { REVIEW_STATUS, DATE_RANGE_FILTERS, ACCESS } = COMMON_CONSTANTS;

const UpcomingFeedbacks = ({ userId, managerId }) => {
  const [flyoutFeedback, setFlyoutFeedback] = useState({});
  const [isFeedbackFlyoutOpen, setIsFeedbackFlyoutOpen] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const [filters, setFilters] = useState({
    feedbackStatus: [REVIEW_STATUS.SENT],
    range: {
      value: DATE_RANGE_FILTERS.THREE_MONTHS.key,
      start: commonDateUtils.getFirstUnixOfDateFromUnix(
        commonDateUtils.getUnixDateFromDaysAgo(90)
      ),
      end: commonDateUtils.getUnixDateFromNow(180)
    },
    requesters: [],
    sort: {
      field: 'scheduledDate',
      order: 'desc'
    },
    currentPage: 1
  });

  const {
    feedbackStatus, range, requesters, sort, currentPage
  } = filters;

  const feedbackFilters = {
    feedbackStatus,
    ...(!isEmpty(requesters)
      ? {
        requestedBy: requesters.map((requester) => requester.id)
        // notRequestedBy: [FEEDBACK_REQUESTED_BY.SYSTEM]
      }
      : {}),
    ...(userId ? { reviewerIds: [userId] } : []),
    ...(managerId ? { reviewerManagerIds: [managerId] } : {}),
    dateKey: 'scheduledDate',
    includeEmptyComments: true,
    includeUnassignedUsers: false
  };
  const loggedUser = appUtils.getLoggedUser();
  if (loggedUser.access !== ACCESS.ADMIN) {
    feedbackFilters.revieweeManagerIds = [loggedUser.id];
    feedbackFilters.reviewerManagerIds = [];
  }

  const {
    data: { userFeedbacks: feedbacks, pagination: feedbackPagination },
    isFetching: isFetchingFeedbacks,
    isError: isErrorFeedbacks,
    isPlaceholderData: isPlaceholderDataFeedbacks
  } = useUserFeedback(
    null,
    {
      filters: feedbackFilters,
      pages: {
        page: currentPage,
        pageSize: 10
      },
      range,
      sort: {
        sortBy: sort.field,
        sortOrder: sort.order
      }
    },
    {
      keepPreviousData: true,
      queryKey: [
        FEEDBACK_QUERY_KEYS.FEEDBACKS,
        FEEDBACK_QUERY_KEYS.UPCOMING_FEEDBACK,
        JSON.stringify(filters)
      ]
    }
  );

  const {
    data: selectedUserAccount,
    isFetching: isFetchingSelectedUserAccount
  } = useAccount(selectedUserId);

  const isInitialFetchingFeedbacks = isFetchingFeedbacks && isPlaceholderDataFeedbacks;
  const isFetching = isInitialFetchingFeedbacks;
  const isError = isErrorFeedbacks;
  const isReady = !isFetching && !isError;

  if (!isReady) {
    return null;
  }

  const openFeedbackFlyout = (feedbackId) => {
    setFlyoutFeedback(() => {
      const feedback = feedbacks.find(
        (f) => f._id === feedbackId || f.id === feedbackId
      );

      if (feedback) {
        setIsFeedbackFlyoutOpen(true);
        return feedback;
      }
    });
  };

  const columns = [
    {
      label: 'Scheduled Date',
      type: TYPES.SORTABLE,
      sort: { ...sort, isSelected: true },
      setSort: (newSort) => {
        setFilters((prevFilters) => ({
          ...prevFilters,
          sort: {
            field: 'scheduledDate',
            order: newSort
          },
          currentPage: 1
        }));
      },
      tooltip: false
    },
    {
      label: 'Reviewer',
      tooltip: true
    },
    {
      label: 'Reviewee',
      tooltip: true
    },
    {
      label: 'Type',
      tooltip: false
    },
    {
      label: 'Status',
      tooltip: false,
      span: 0.75
    },
    {
      label: 'Requester',
      tooltip: false
    },
    {
      type: TYPES.ACTION,
      label: 'View',
      hidden: true,
      span: 0.5,
      tooltip: false
    }
  ];

  const rows = feedbacks.map((review) => {
    const {
      id,
      scheduledDate,
      reviewerName,
      reviewerImage,
      reviewee,
      revieweeName,
      revieweeImage,
      feedbackType,
      status,
      requesterName,
      requesterImage
    } = review;

    const formattedDate = commonDateUtils.dateToMonthDayYearFormat(
      new Date(scheduledDate)
    );

    const openRevieweeModal = () => {
      setSelectedUserId(reviewee);
      setIsModalOpen(true);
    };

    const viewAction = () => openFeedbackFlyout(id);

    const { firstName: reviewerFirstName, lastName: reviewerLastName } = commonUtils.getFirstAndLastName(reviewerName);
    const { firstName: revieweeFirstName, lastName: revieweeLastName } = commonUtils.getFirstAndLastName(revieweeName);

    return [
      { label: formattedDate },
      {
        image: reviewerImage,
        label: commonUtils.getFullName(
          reviewerFirstName,
          reviewerLastName,
          'lastName'
        ),
        type: TYPES.IMAGE_WITH_TEXT,
        disabled: true,
        discrete: true
      },
      {
        image: revieweeImage,
        label: commonUtils.getFullName(
          revieweeFirstName,
          revieweeLastName,
          'lastName'
        ),
        type: TYPES.IMAGE_WITH_TEXT,
        onClick: openRevieweeModal
      },
      {
        label: feedbackType
      },
      {
        label: status
      },
      {
        image: requesterImage,
        label: requesterName,
        type: TYPES.IMAGE_WITH_TEXT,
        disabled: true,
        discrete: true
      },
      {
        label: 'View',
        type: TYPES.ACTION,
        onClick: viewAction
      }
    ];
  });

  const pagination = {
    currentPage,
    setCurrentPage: (page) => {
      setFilters((prevFilters) => ({
        ...prevFilters,
        currentPage: page
      }));
    },
    totalPages: feedbackPagination.pages
  };

  return (
    <>
      <FeedbackFlyout
        width={40}
        feedback={flyoutFeedback}
        isOpen={isFeedbackFlyoutOpen}
        setIsOpen={setIsFeedbackFlyoutOpen}
        showXButton={false}
      />
      <AlertModal
        isOpen={isModalOpen}
        close={() => setIsModalOpen(false)}
        onAction={() => {
          window.open(appUtils.getDashRoute(selectedUserId), '_blank');
          setIsModalOpen(false);
          setSelectedUserId(null);
        }}
        title={get(selectedUserAccount, 'name', 'View More Details')}
        content={`View ${get(selectedUserAccount, 'firstName', 'this user')}'s dashboard see more details.`}
        actionText='View Dashboard'
        isLoading={isFetchingSelectedUserAccount}
        type='success'
        customIcon={(
          <CircleV2
            imageUrl={get(selectedUserAccount, 'imageUrl', null)}
            size='sm'
          />
        )}
      />
      <Base
        classes={`${STYLE.CONTAINER_WHITE} !p-4`}
        loading={isFetchingFeedbacks}
      >
        <p className='text-xl text-black font-bold mb-0 mr-8'>
          Feedback Requests
        </p>
        <p className='text-md font-normal my-2 leading-5 text-bluish-gray mb-6'>
          List of feedback requests for this team member.
        </p>
        <UpcomingFeedbacksFilters filters={filters} setFilters={setFilters} />
        <Table columns={columns} rows={rows} pagination={pagination} />
      </Base>
    </>
  );
};

export default UpcomingFeedbacks;
